import { IconCheck, IconCreditCard, IconProps } from '@tabler/icons-react';
import {
  CreditCardIcon,
  HexagonalExclamationIcon,
  InfoSquareRounded,
} from '@zorro/shared/assets';
import {
  formatDateEnLocaleMonthDay,
  getFirstDayOfMonthEqualToOrBeforeDate,
} from '@zorro/shared/formatters';
import { CSSProperties, ElementType, ReactNode } from 'react';

import {
  AUTO_PAY_LEARN_MORE_LINK,
  INITIAL_PREMIUM_LEARN_MORE_LINK,
} from '../../Payments/PaymentStatus/PaymentStatusUtils';

export type IconWithStyles = {
  component: ElementType<IconProps>;
  size?: number | string;
  styles?: CSSProperties;
};

export enum FlagType {
  AOR = 'AOR',
  SELF_ENROLL = 'SELF_ENROLL',
  CARRIER_APP_SENT = 'CARRIER_APP_SENT',
  INITIAL_PAYMENT = 'INITIAL_PAYMENT',
  AUTO_PAY = 'AUTO_PAY',
  AUTO_PAY_ENROLLMENT_CONFIRMED = 'AUTO_PAY_ENROLLMENT_CONFIRMED',
  NEXT_STEPS = 'NEXT_STEPS',
  RENEWAL = 'RENEWAL',
  MEDICARE = 'MEDICARE',
  MEDICARE_IFP = 'MEDICARE_IFP',
  GENERAL = 'GENERAL',
}

export type CommunicationFlagItem = {
  type: FlagType;
  body: string | string[] | ReactNode;
  icon?: IconWithStyles;
  title?: string;
  description?: string | ReactNode;
  warning?: string;
  link?: {
    label: string;
    url: string;
  };
  button?: {
    label: string;
    onClick: (date?: string | null) => void | Promise<void>;
    variant?: string;
    leftSection?: ElementType<IconProps>;
    rightSection?: ElementType<IconProps>;
  };
  secondaryButton?: {
    label: string;
    variant?: string;
    leftSection?: ElementType<IconProps>;
    rightSection?: ElementType<IconProps>;
  };
  isSummaryPage?: boolean;
  filesPageUrl?: string;
};

export type CommunicationMessageConfig = {
  sectionTitle?: string;
  flags: CommunicationFlagItem[];
};

export const SECTION_TITLES = {
  ACTION_NEEDED: 'Action needed',
  ACTION_TO_FINALIZE: 'Action required',
} as const;

const startOfEffectiveMonth = (effectiveFrom: string) => {
  return formatDateEnLocaleMonthDay(
    getFirstDayOfMonthEqualToOrBeforeDate(effectiveFrom)
  );
};

type AorFlagContentProps = {
  employeeName?: string;
  priorEnrollmentEffectiveUntil: string | null;
};
type SelfEnrollFlagContentProps = {
  employeeName?: string;
  isSummaryPage?: boolean;
};

export const SELF_ENROLL_COPY = {
  instructions: (isSummaryPage?: boolean) =>
    `The carrier you chose requires you to submit your enrollment application directly. Zorro cannot complete this application for you. ${
      isSummaryPage
        ? 'Please go to your Zorro homepage and follow the instructions there to submit your application.'
        : 'Please follow the instructions in the link below to make sure you have coverage.'
    }`,
  warning: 'If you do not submit your application, you will not have coverage.',
} as const;

export const FLAG_CONTENT = {
  AOR: ({
    employeeName,
    priorEnrollmentEffectiveUntil,
  }: AorFlagContentProps) => ({
    title: employeeName
      ? `${employeeName} must cancel their current plan`
      : 'Cancel your current plan',
    body: employeeName
      ? `As part of ${employeeName}'s move to Zorro, starting ${priorEnrollmentEffectiveUntil ? startOfEffectiveMonth(priorEnrollmentEffectiveUntil) : 'December 1'}, they must cancel the current plan effective ${priorEnrollmentEffectiveUntil ? formatDateEnLocaleMonthDay(priorEnrollmentEffectiveUntil) : 'December 31'}, to avoid potential tax penalties, coverage gaps, and paying for duplicate coverage. Only ${employeeName} can cancel it, by calling their carrier (or the exchange, if they enrolled there). Zorro can't do this for them.`
      : `As part of your move to Zorro, starting ${priorEnrollmentEffectiveUntil ? startOfEffectiveMonth(priorEnrollmentEffectiveUntil) : 'December 1'}, you must cancel your current plan effective ${priorEnrollmentEffectiveUntil ? formatDateEnLocaleMonthDay(priorEnrollmentEffectiveUntil) : 'December 31'}, to avoid potential tax penalties, coverage gaps, and paying for duplicate coverage. You must cancel it yourself; Zorro can’t do this for you. If enrolled directly with the carrier, call the carrier. If enrolled via the healthcare exchange, call the exchange.`,
  }),
  SELF_ENROLL: ({
    employeeName,
    isSummaryPage,
  }: SelfEnrollFlagContentProps) => ({
    title: employeeName
      ? `${employeeName} must submit their application`
      : 'Manually submit your application',
    body: employeeName
      ? `This carrier requires the member to submit their enrollment application directly. Zorro can't submit it for them. They won't have coverage until they do. Step-by-step instructions are available in their Zorro account.`
      : `${SELF_ENROLL_COPY.instructions(isSummaryPage)} IMPORTANT: ${SELF_ENROLL_COPY.warning}`,
  }),
  CARRIER_APP_SENT: (employeeName?: string) => ({
    title: employeeName
      ? 'Carrier requirement pending'
      : 'Carrier requirements',
  }),
  INITIAL_PAYMENT: (employeeName?: string) => ({
    title: employeeName
      ? `${employeeName} must make their first premium payment`
      : 'Set up your first premium payment',
    body: employeeName
      ? `${employeeName} needs to make their first premium payment in the carrier portal, using their Zorro Pay details.`
      : 'Set up the first payment, using Zorro Pay card details, in your carrier portal.',
    linkLabel: 'Learn more',
    linkUrl: INITIAL_PREMIUM_LEARN_MORE_LINK,
  }),
  AUTO_PAY: (employeeName?: string) => ({
    title: employeeName
      ? `${employeeName} must set up auto-pay`
      : 'Set up auto-pay',
    body: employeeName
      ? `The carrier requires ${employeeName} to set up auto-pay in the carrier portal to avoid a coverage interruption. Some carriers allow this right away, others only once coverage starts. ${employeeName} should use their Zorro Pay details.`
      : 'Your carrier requires members to set up auto-pay directly in the carrier portal to avoid coverage interruptions.\nTiming varies by carrier. Some allow setup right away, others only once coverage starts. Check your portal now. If auto-pay is available, use your Zorro Pay details to set it up. If not, check again after your plan’s start date.',
    linkLabel: 'Learn more',
    linkUrl: AUTO_PAY_LEARN_MORE_LINK,
  }),
  AUTO_PAY_ENROLLMENT_CONFIRMED: (employeeName?: string) => ({
    title: employeeName
      ? `${employeeName} must turn on auto-pay`
      : 'Turn on auto-pay',
    body: 'Auto pay setup ensures timely premium payments and uninterrupted coverage',
    linkLabel: 'Learn more',
    linkUrl: AUTO_PAY_LEARN_MORE_LINK,
  }),
  NEXT_STEPS: (employeeName?: string) => ({
    title: employeeName
      ? 'Next steps that are required from the employee'
      : 'What to expect next',
    initialPayments: employeeName
      ? `Once the carrier gets the application, ${employeeName} will need to make their first premium payment in the carrier portal, using their Zorro Pay details.`
      : 'Once the application is submitted to the carrier, you will need to set up the first payment yourself in your carrier portal using the Zorro Pay details, available in your Zorro account.',
    autoPay: employeeName
      ? `Once the carrier gets the application and the first payment is made, ${employeeName} will need to set up auto-pay in the carrier portal, using their Zorro Pay details.`
      : 'Once the application is submitted to the carrier, and the first payment is completed, you will need to set up auto-pay yourself, in your carrier portal, using Zorro Pay, available in your Zorro account.',
  }),
} as const;

export const BUTTON_LABELS = {
  MARK_AS_DONE: 'Mark as set up',
  VIEW_ZORRO_PAY: 'View Zorro Pay',
} as const;

export const COMMUNICATION_ICONS = {
  ALERT_CIRCLE: {
    component: HexagonalExclamationIcon,
    size: 'md',
    styles: {
      backgroundColor: '#FFD7D0',
      borderRadius: '4px',
      padding: '0.25rem',
    },
  },
  INFO_CIRCLE: {
    component: InfoSquareRounded,
    size: 'md',
    styles: {
      backgroundColor: '#E0E3FE',
      borderRadius: '4px',
      padding: '0.25rem',
    },
  },
  CREDIT_CARD: {
    component: CreditCardIcon,
    size: 'md',
    styles: {
      backgroundColor: '#FFD7D0',
      borderRadius: '4px',
      padding: '0.25rem',
    },
  },
  CHECK: IconCheck,
  CREDIT_CARD_ICON: IconCreditCard,
} as const;
